/*
 * Seven Pages: one-tap bundle and add-on buttons.
 * - [data-sp-pair] card on locket pages: adds this locket + the second locket picked in the dropdown.
 * - [data-sp-add] buttons in the cart drawer: adds the items in the button's JSON.
 * Uses the theme's own cart flow (CartLinesUpdateEvent from @shopify/events), so the cart drawer
 * refreshes and opens exactly as it does for the regular Add to cart button.
 * If that module can't load, it falls back to the cart page after adding.
 */

let eventsModule;

async function loadEvents() {
  if (eventsModule !== undefined) return eventsModule;
  try {
    eventsModule = await import('@shopify/events');
  } catch (error) {
    eventsModule = null;
  }
  return eventsModule;
}

function routes() {
  const fallback = { cart_add_url: '/cart/add.js', cart_url: '/cart' };
  return (window.Theme && window.Theme.routes) || fallback;
}

function cartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((element) => {
    const id = element instanceof HTMLElement ? element.dataset.sectionId : null;
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids;
}

function showError(scope, message) {
  const box = scope ? scope.querySelector('[data-sp-error]') : null;
  if (!box) return;
  box.textContent = message;
  box.hidden = false;
  window.setTimeout(() => {
    box.hidden = true;
  }, 8000);
}

async function addItems(items, button, scope) {
  if (!items.length || button.getAttribute('aria-busy') === 'true') return;

  const { cart_add_url: addUrl, cart_url: cartUrl } = routes();
  const sections = cartSectionIds();
  const events = await loadEvents();
  const UpdateEvent = events && events.CartLinesUpdateEvent;
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  button.setAttribute('aria-busy', 'true');
  button.disabled = true;

  let deferred = null;
  if (UpdateEvent) {
    deferred = UpdateEvent.createPromise();
    button.dispatchEvent(
      new UpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );
  }

  try {
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    });
    const data = await response.json();
    const failed = !response.ok || Boolean(data.status);

    if (deferred) {
      const cart = await fetch(`${cartUrl}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: UpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'sp-offers',
          sourceId: button.id || 'sp-offers',
          itemCount,
          sections: failed ? undefined : data.sections,
          didError: failed,
        },
      });
    }

    if (failed) {
      showError(scope, data.description || data.message || 'That could not be added. Please try again.');
      return;
    }

    if (!deferred) window.location.assign(cartUrl);
  } catch (error) {
    if (deferred) deferred.reject(error);
    showError(scope, 'That could not be added. Please check your connection and try again.');
  } finally {
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }
}

function currentVariantId(card) {
  const section = card.closest('.shopify-section') || document;
  const input = section.querySelector('product-form-component input[name="id"]');
  const value = Number(input && input.value);
  return value || Number(card.dataset.currentVariant);
}

function syncPairCard(select) {
  const card = select.closest('[data-sp-pair]');
  const option = select.selectedOptions[0];
  if (!card || !option) return;
  const set = (selector, text) => {
    const element = card.querySelector(selector);
    if (element && text) element.textContent = text;
  };
  set('[data-sp-pair-total]', option.dataset.pair);
  set('[data-sp-pair-was]', option.dataset.was);
  set('[data-sp-pair-save]', option.dataset.save);
  const image = card.querySelector('[data-sp-pair-img]');
  if (image && option.dataset.img) image.src = option.dataset.img;
}

// Horizon measures the drawer's sticky checkout bar before the new cart lines finish rendering,
// so after a first add the Check out button can end up below the fold. Re-measure once rendering settles.
function remeasureCartDrawer() {
  const items = document.querySelector('#cart-drawer cart-items-component');
  if (items) items.dispatchEvent(new Event('cart-section:restored', { bubbles: true }));
}

loadEvents().then((events) => {
  const name = events && events.StandardEvents && events.StandardEvents.cartLinesUpdate;
  if (!name) return;
  document.addEventListener(name, (event) => {
    if (!event.promise) return;
    event.promise
      .then(() => {
        window.setTimeout(remeasureCartDrawer, 300);
        window.setTimeout(remeasureCartDrawer, 1000);
      })
      .catch(() => {});
  });
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (target instanceof HTMLSelectElement && target.matches('[data-sp-pair-select]')) syncPairCard(target);
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const pairButton = target.closest('[data-sp-pair-submit]');
  if (pairButton) {
    event.preventDefault();
    const card = pairButton.closest('[data-sp-pair]');
    const select = card && card.querySelector('[data-sp-pair-select]');
    if (!card || !select) return;
    const first = currentVariantId(card);
    const second = Number(select.value);
    if (!first || !second) return;
    const items = first === second ? [{ id: first, quantity: 2 }] : [{ id: first, quantity: 1 }, { id: second, quantity: 1 }];
    addItems(items, pairButton, card);
    return;
  }

  const addButton = target.closest('[data-sp-add]');
  if (addButton) {
    event.preventDefault();
    let items = [];
    try {
      items = JSON.parse(addButton.getAttribute('data-sp-add') || '[]')
        .map((item) => ({ id: Number(item.id), quantity: Number(item.quantity) || 1 }))
        .filter((item) => item.id);
    } catch (error) {
      return;
    }
    addItems(items, addButton, addButton.closest('[data-sp-offers]'));
  }
});